import { io, Socket } from 'socket.io-client';

// In production, use the same origin (relative URL) so it goes through Nginx proxy
// In development, use the direct service URL
const SOCKET_URL = import.meta.env.PROD 
  ? window.location.origin 
  : (import.meta.env.VITE_SOCKET_URL || 'http://localhost:19008');
// Socket.io uses /socket.io as the default path
// In production, we need /stock/socket.io because the app is served under /stock
// But check Nginx config - it should proxy /stock/socket.io/ to the notifications service
const SOCKET_PATH = import.meta.env.PROD ? '/stock/socket.io/' : '/socket.io';

// MVP: Disable WebSocket in production until notifications service is ready
const WEBSOCKET_ENABLED = import.meta.env.VITE_WEBSOCKET_ENABLED === 'true' || !import.meta.env.PROD;

// Event types for type safety
export interface TransactionCreatedEvent {
  id: string;
  type: 'sale' | 'purchase' | 'transfer' | 'issue' | 'return';
  invoiceNo: string;
  amount: number;
  outlet: string;
  timestamp: string;
}

export interface InventoryUpdatedEvent {
  itemCode: string;
  itemName: string;
  outlet: string;
  previousQty: number;
  newQty: number;
  timestamp: string;
}

export interface LowStockAlertEvent {
  itemCode: string;
  itemName: string;
  outlet: string;
  currentQty: number;
  minimumQty: number;
  timestamp: string;
}

export interface NotificationEvent {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp: string;
}

export type SocketEventMap = {
  'transaction:created': TransactionCreatedEvent;
  'transaction:updated': TransactionCreatedEvent;
  'inventory:updated': InventoryUpdatedEvent;
  'inventory:low_stock': LowStockAlertEvent;
  'notification:new': NotificationEvent;
  'dashboard:refresh': void;
  connect: void;
  disconnect: void;
  connect_error: Error;
};

class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private listeners: Map<string, Set<(...args: unknown[]) => void>> = new Map();

  connect(token: string): Socket {
    if (this.socket?.connected) {
      return this.socket;
    }

    // MVP: Skip connection if WebSocket is disabled
    if (!WEBSOCKET_ENABLED) {
      console.log('[Socket] WebSocket disabled in production (MVP mode)');
      // Return a dummy socket that does nothing
      return null as unknown as Socket;
    }

    this.socket = io(SOCKET_URL, {
      auth: { token },
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    this.setupDefaultListeners();
    this.reattachListeners();

    return this.socket;
  }

  private setupDefaultListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('[Socket] Connected to notifications server');
      this.reconnectAttempts = 0;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('[Socket] Max reconnection attempts reached');
      }
    });
  }

  private reattachListeners(): void {
    if (!this.socket) return;

    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        this.socket?.on(event, callback);
      });
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.reconnectAttempts = 0;
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  on<K extends keyof SocketEventMap>(
    event: K,
    callback: (data: SocketEventMap[K]) => void
  ): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback as (...args: unknown[]) => void);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket?.on(event as string, callback as any);
  }

  off<K extends keyof SocketEventMap>(event: K, callback?: (data: SocketEventMap[K]) => void): void {
    if (callback) {
      this.listeners.get(event)?.delete(callback as (...args: unknown[]) => void);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.socket?.off(event as string, callback as any);
    } else {
      this.listeners.delete(event);
      this.socket?.off(event as string);
    }
  }

  emit<K extends string>(event: K, data?: unknown): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('[Socket] Cannot emit event, socket not connected');
    }
  }

  // Subscribe to a room (e.g., for company-specific updates)
  joinRoom(room: string): void {
    this.emit('join:room', { room });
  }

  // Leave a room
  leaveRoom(room: string): void {
    this.emit('leave:room', { room });
  }
}

export const socketService = new SocketService();
export default socketService;
